import React, { memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Formik, FormikProps, validateYupSchema, yupToFormErrors } from 'formik'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { SectionCard } from 'src/components/shared/SectionCard'
import {
  ONBOARDING_STEP_FIELDS,
  ONBOARDING_STEPS,
  OnboardingStep,
} from 'src/constants/payments-ops/bankOnboarding'
import type { BankOnboardingFormValues } from 'src/interfaces/bankOnboarding'
import { getBankOnboardingSubmitting } from 'src/selectors/bankOnboarding'
import { buildOnboardingStepValidator } from './onboardingSteps.validator'
import { OnboardingStepper } from './OnboardingStepper'
import { AttestorStep } from './steps/AttestorStep'
import { BeneficialOwnersStep } from './steps/BeneficialOwnersStep'
import { BusinessAddressStep } from './steps/BusinessAddressStep'
import { BusinessStep } from './steps/BusinessStep'
import { ControlPersonStep } from './steps/ControlPersonStep'
import { PartnerStep } from './steps/PartnerStep'
import { ReviewStep } from './steps/ReviewStep'

interface Props {
  initialValues: BankOnboardingFormValues
  blockedPartnerReasons: Record<string, string>
  /** Called from the Review step's confirmed submit; resolves `false` when the request failed. */
  onSubmit(values: BankOnboardingFormValues): Promise<boolean>
}

const STEP_ORDER = ONBOARDING_STEPS.map(({ step }) => {
  return step
})

const preventNativeSubmit = (event: React.FormEvent<HTMLFormElement>) => {
  event.preventDefault()
}

const NewOnboardingWizardComponent = ({
  initialValues,
  blockedPartnerReasons,
  onSubmit,
}: Props) => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(STEP_ORDER[0])
  const [completedSteps, setCompletedSteps] = useState<OnboardingStep[]>([])
  const isSubmitting = useSelector(getBankOnboardingSubmitting)

  const currentIndex = STEP_ORDER.indexOf(currentStep)
  const isFirstStep = currentIndex === 0
  const isReviewStep = currentStep === OnboardingStep.Review

  const currentTitle = useMemo(() => {
    return ONBOARDING_STEPS[currentIndex]?.title ?? ''
  }, [currentIndex])

  /**
   * Validates ONE step. On failure the step's errors are set and its fields marked touched so the
   * wrappers show them (they render an error only when `touched[name]`).
   */
  const validateStep = useCallback(
    async (
      formik: FormikProps<BankOnboardingFormValues>,
      step: OnboardingStep,
    ): Promise<boolean> => {
      const schema = buildOnboardingStepValidator(step, { blockedPartnerReasons })

      try {
        await validateYupSchema(formik.values, schema)
      } catch (validationError: unknown) {
        formik.setErrors(yupToFormErrors(validationError))
        formik.setTouched(
          ONBOARDING_STEP_FIELDS[step].reduce<Record<string, boolean>>((touched, field) => {
            return { ...touched, [field]: true }
          }, {}),
          false,
        )

        return false
      }

      formik.setErrors({})

      return true
    },
    [blockedPartnerReasons],
  )

  /**
   * Formik's own validation (run on blur): the CURRENT step's schema only, so leaving a field
   * re-checks it — and clears a stale error — without flagging fields of steps not yet reached.
   * A field's error still shows only once it is touched.
   */
  const validateCurrentStep = useCallback(
    async (values: BankOnboardingFormValues) => {
      try {
        await validateYupSchema(
          values,
          buildOnboardingStepValidator(currentStep, { blockedPartnerReasons }),
        )
      } catch (validationError: unknown) {
        return yupToFormErrors<BankOnboardingFormValues>(validationError)
      }

      return {}
    },
    [blockedPartnerReasons, currentStep],
  )

  const handleNext = useCallback(
    async (formik: FormikProps<BankOnboardingFormValues>) => {
      const isValid = await validateStep(formik, currentStep)

      if (!isValid) {
        return
      }

      setCompletedSteps((previousSteps) => {
        return previousSteps.includes(currentStep) ? previousSteps : [...previousSteps, currentStep]
      })
      setCurrentStep(STEP_ORDER[currentIndex + 1])
    },
    [currentIndex, currentStep, validateStep],
  )

  /**
   * A completed step clicked in the stepper. Back is free; forward first validates the step being
   * left, exactly as Next does, so a jump can never skip an invalid step. Steps in between were
   * valid when completed, and the submit re-checks every step anyway.
   */
  const handleStepClick = useCallback(
    async (formik: FormikProps<BankOnboardingFormValues>, step: OnboardingStep) => {
      if (STEP_ORDER.indexOf(step) < currentIndex) {
        setCurrentStep(step)

        return
      }

      const isValid = await validateStep(formik, currentStep)

      if (!isValid) {
        return
      }

      setCompletedSteps((previousSteps) => {
        return previousSteps.includes(currentStep) ? previousSteps : [...previousSteps, currentStep]
      })
      setCurrentStep(step)
    },
    [currentIndex, currentStep, validateStep],
  )

  const handleBack = useCallback(() => {
    setCurrentStep(STEP_ORDER[Math.max(currentIndex - 1, 0)])
  }, [currentIndex])

  /** Re-checks every step against the live values — an earlier step may have been edited since. */
  const validateAllSteps = useCallback(
    async (values: BankOnboardingFormValues): Promise<OnboardingStep | null> => {
      for (const step of STEP_ORDER) {
        try {
          await validateYupSchema(
            values,
            buildOnboardingStepValidator(step, { blockedPartnerReasons }),
          )
        } catch {
          return step
        }
      }

      return null
    },
    [blockedPartnerReasons],
  )

  /** Jumps to the step that no longer validates and shows its errors. */
  const handleInvalidStep = useCallback(
    async (formik: FormikProps<BankOnboardingFormValues>, step: OnboardingStep) => {
      setCompletedSteps((previousSteps) => {
        return previousSteps.filter((completedStep) => {
          return completedStep !== step
        })
      })
      setCurrentStep(step)
      await validateStep(formik, step)
    },
    [validateStep],
  )

  const renderStep = (formik: FormikProps<BankOnboardingFormValues>) => {
    switch (currentStep) {
      case OnboardingStep.PartnerAndProgramManager:
        return <PartnerStep blockedPartnerReasons={blockedPartnerReasons} />
      case OnboardingStep.Business:
        return <BusinessStep />
      case OnboardingStep.BusinessAddress:
        return <BusinessAddressStep />
      case OnboardingStep.BeneficialOwners:
        return <BeneficialOwnersStep />
      case OnboardingStep.ControlPerson:
        return <ControlPersonStep />
      case OnboardingStep.Attestor:
        return <AttestorStep />
      case OnboardingStep.Review:
        return (
          <ReviewStep
            onBack={handleBack}
            onSubmit={onSubmit}
            validateAllSteps={validateAllSteps}
            onInvalidStep={(step) => {
              handleInvalidStep(formik, step)
            }}
          />
        )
      default:
        return null
    }
  }

  return (
    <Formik<BankOnboardingFormValues>
      initialValues={initialValues}
      validate={validateCurrentStep}
      validateOnChange={false}
      validateOnBlur
      onSubmit={() => {
        return undefined
      }}
    >
      {(formik) => {
        return (
          <form
            noValidate
            onSubmit={preventNativeSubmit}
            css={tw`flex min-h-0 flex-1 flex-col gap-4 overflow-auto`}
          >
            <OnboardingStepper
              currentStep={currentStep}
              completedSteps={completedSteps}
              isLocked={isSubmitting}
              onStepClick={(step) => {
                handleStepClick(formik, step)
              }}
            />
            <SectionCard title={currentTitle}>{renderStep(formik)}</SectionCard>
            {isReviewStep ? null : (
              <div css={tw`flex shrink-0 flex-row justify-between gap-2`}>
                <Button size="small" color="grey" disabled={isFirstStep} onClick={handleBack}>
                  ← Back
                </Button>
                <Button
                  size="small"
                  color="dark"
                  onClick={() => {
                    handleNext(formik)
                  }}
                  data-testid="onboarding-next"
                >
                  Next →
                </Button>
              </div>
            )}
          </form>
        )
      }}
    </Formik>
  )
}

export const NewOnboardingWizard = memo(NewOnboardingWizardComponent)
